import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';
import { Button } from '../../components/ui/Button';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { FiShield, FiChevronLeft, FiEye } from 'react-icons/fi';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  padding: ${theme.spacing[6]};
  max-width: 600px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  background: none;
  border: none;
  color: ${theme.colors.primary[500]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  padding: ${theme.spacing[2]} 0;
  margin-bottom: ${theme.spacing[4]};
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const PageDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const FormCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  padding: ${theme.spacing[6]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  padding-right: ${theme.spacing[10]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.light.text.muted};
  }
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: ${theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.light.text.primary};
  }
`;

const ErrorText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.accent.danger};
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[2]};
`;

const ChangePasswordPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({
        newPassword: '',
        confirmPassword: '',
    });

    const validateForm = () => {
        const newErrors = {
            newPassword: '',
            confirmPassword: '',
        };

        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return !newErrors.newPassword && !newErrors.confirmPassword;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!user) {
            toast.error('User not authenticated');
            return;
        }

        setLoading(true);

        try {
            // Get the ID token
            const idToken = await user.getIdToken();

            // Call the PHP backend
            const endpoint = process.env.REACT_APP_CHANGE_PASSWORD_ENDPOINT || 'https://guards.dynamixzone.com/api/change-password.php';

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    idToken,
                    newPassword: formData.newPassword,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                toast.success('Password changed successfully!');
                setFormData({ newPassword: '', confirmPassword: '' });
                setTimeout(() => {
                    navigate('/profile');
                }, 1500);
            } else {
                toast.error(data.message || 'Failed to change password');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            toast.error('An error occurred while changing password');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    return (
        <DashboardLayout>
            <PageContainer>
                <BackButton onClick={() => navigate('/profile')}>
                    <FiChevronLeft size={16} />
                    Back to Profile
                </BackButton>

                <PageHeader>
                    <PageTitle>Change Password</PageTitle>
                    <PageDescription>Update your account password securely</PageDescription>
                </PageHeader>

                <FormCard>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label htmlFor="newPassword">New Password</Label>
                            <InputWrapper>
                                <Input
                                    type={showNewPassword ? 'text' : 'password'}
                                    id="newPassword"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    placeholder="Enter new password"
                                    disabled={loading}
                                />
                                <TogglePasswordButton
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    tabIndex={-1}
                                >
                                    {showNewPassword ? <FiEye size={18} /> : <FiEye size={18} />}
                                </TogglePasswordButton>
                            </InputWrapper>
                            {errors.newPassword && <ErrorText>{errors.newPassword}</ErrorText>}
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                            <InputWrapper>
                                <Input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    placeholder="Confirm new password"
                                    disabled={loading}
                                />
                                <TogglePasswordButton
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    tabIndex={-1}
                                >
                                    {showConfirmPassword ? <FiEye size={18} /> : <FiEye size={18} />}
                                </TogglePasswordButton>
                            </InputWrapper>
                            {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
                        </FormGroup>

                        <ButtonGroup>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={loading}
                            >
                                <FiShield size={16} />
                                {loading ? 'Changing Password...' : 'Change Password'}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => navigate('/profile')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </ButtonGroup>
                    </Form>
                </FormCard>
            </PageContainer>
        </DashboardLayout>
    );
};

export default ChangePasswordPage;
